import React, { useEffect, useState } from 'react';
import {
  Accordion,
  Box,
  ChangeRequestDialog,
  ContentTemplate,
  DatePicker,
  Grid,
  PAYLOAD_DATE_FORMAT,
  Progress,
  SectionHeader,
  TitleHeader,
  tomorrowDate,
  useSnackbarHandler,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FunctionModule } from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import EditButton from 'web-library/components/buttons/EditButton';
import { AUTHORITY_NAMES, FUNCTION_NAMES } from 'web-library/models/AuthorityFunctions';
import {
  AccountManagementActionTypesResponseObject,
  CoordinatorProfileResponse,
} from 'web-library/models/AccountManagementConfiguration';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { MutationStatus } from 'react-query';
import { stringToSemanticDisplay } from 'web-library/utils/DateUtils';
import { usePostSearchApprovers } from '../../../api-refactored/user-group-access-configuration/usePostSearchApprovers';
import { useGetCoordinatorProfile } from '../../../api-refactored/account-management/useGetCoordinatorProfile';
import { usePutUpdateCoordinatorProfile } from '../../../api-refactored/account-management/usePutUpdateCoordinatorProfile';
import CoordinatorContactInformation from './CoordinatorContactInformation';
import CoordinatorPersonalParticulars from './CoordinatorPersonalParticulars';
import CreateEditCoordinatorSchema, {
  CreateEditCoordinatorSchemaData,
} from './CreateEditCoordinatorSchema';
import { usePostCreateCoordinatorProfile } from '../../../api-refactored/account-management/usePostCreateCoordinatorProfile';
import { usePostAccountManagementAction } from '../../../api-refactored/account-management/usePostAccountManagementAction';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';
import { usePostRequestFilePresigned } from '../../../api-refactored/user-group-access-configuration/usePostRequestFilePresigned';
import ManageAccountContent from '../CreateEditViewAdminAccountDetails/ManageAccountContent';
import ActionApproverRemarksContent from '../CreateEditViewAdminAccountDetails/ActionApproverRemarksContent';
import AccountManagementMiscellaneousComponents from '../CreateEditViewAdminAccountDetails/AccountManagementMiscellaneousComponents';
import { useStyles } from '../CreateEditViewAdminAccountDetails';
import {
  useCreateEditViewCoordinatorAccountDetails,
  useEditViewCoordinatorFormValues,
} from './useCreateEditViewCoordinatorAccountDetails';
import ToggleDisplayEditFormItem from '../../../components/ToggleDisplayEditFormItem';
import dayjs from 'dayjs';
import ControlledDatePicker from 'web-library/stories/atom/DatePicker/ControlledDatePicker';

export interface CreateEditViewCoordinatorProps {
  isEdit: boolean;
  isCreate: boolean;
}

const CreateEditViewCoordinatorAccount = ({ isEdit, isCreate }: CreateEditViewCoordinatorProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const { uuid } = useParams<{ uuid: string }>();

  const [isEditState, setIsEditState] = useState<boolean>(!isEdit);
  const [isCreateState] = useState<boolean>(isCreate);
  const [isApproverNeededAction, setIsApproverNeededAction] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [filteredActionTypes, setFilteredActionTypes] = useState<
    AccountManagementActionTypesResponseObject[]
  >([]);

  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const [files, setFiles] = useState<UploadedFile[]>([]);

  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);

  const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState<string>('');

  const [openConfirmChangesModal, setOpenConfirmChangesModal] = useState<boolean>(false);

  const [deleteAccountModalMessage, setDeleteAccountModalMessage] = useState<string>('');

  const {
    data: coordinatorProfileDetails,
    refetch: refetchCoordinatorProfileDetails,
  } = useGetCoordinatorProfile({
    uuid,
    enabled: !!uuid && isEdit,
    onSuccess: () => {
      setFiles([]);
      setErrorMessage('');
      setUploadFileErrorMessage('');
      refetchAccountManagementActionTypes();
    },
  });
  const [expiryDate, setExpiryDate] = useState<string>(
    coordinatorProfileDetails?.coordinatorDetail.expiryDate || ''
  );
  const { data: authorities } = useGetUserAuthorities();

  const {
    mutate: mutatePostSearchApprovers,
    data: postSearchApproversData,
  } = usePostSearchApprovers({});

  useEffect(() => {
    mutatePostSearchApprovers({
      functionName: FUNCTION_NAMES.USER_ACCOUNTS,
      functionModule: FunctionModule.ADMIN,
    });
  }, [mutatePostSearchApprovers]);

  const { mutate: mutatePostAccountManagementAction } = usePostAccountManagementAction({
    onSuccess: () => {
      setErrorMessage('');
      setApiButtonStatus('success');
      handleBackToCoordinatorListingWithAlert();
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.message!);
      setApiButtonStatus('error');
    },
  });

  const hasEditPermission = authorities?.includes(AUTHORITY_NAMES.CP_ADMIN_USER_ACCOUNTS_EDIT);

  const { disabledFields, formDefaultValues } = useEditViewCoordinatorFormValues({
    coordinatorProfileDetails: coordinatorProfileDetails as CoordinatorProfileResponse,
    isEditState,
    isApproverNeededAction,
    isCreateState,
  });

  const formHook = useForm<CreateEditCoordinatorSchemaData>({
    mode: 'onSubmit',
    resolver: joiResolver(
      CreateEditCoordinatorSchema(isEdit || isEditState ? disabledFields : undefined)
    ),
    defaultValues: formDefaultValues,
  });

  const { handleSubmit, getValues, watch, control } = formHook;

  const watchActionType = watch('action');

  const { mutate: mutatePutUpdateCoordinatorProfile } = usePutUpdateCoordinatorProfile({
    uuid,
    onSuccess: () => {
      setApiButtonStatus('success');
      setShouldSubmit(false);
      setIsEditState(false);
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>UserAccountManagement.ActionRequestMessage', {
          action: 'Edit particulars',
        }),
      });
      refetchCoordinatorProfileDetails().then(() => null);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.message!);
      setApiButtonStatus('error');
    },
  });

  const { mutate: mutatePostCreateCoordinatorProfile } = usePostCreateCoordinatorProfile({
    onSuccess: () => {
      setErrorMessage('');
      setApiButtonStatus('success');
      handleBackToCoordinatorListing();
    },
    onError: (err) => {
      setErrorMessage(err?.response?.data?.message!);
      setApiButtonStatus('error');
    },
  });

  const { mutate: postRequestFilePresigned } = usePostRequestFilePresigned({
    onSuccess: (fileDataContent) => {
      const tempFiles: UploadedFile[] = [];
      fileDataContent.forEach((_value, index) => {
        const pendingFile = files[index];
        const fileData = fileDataContent[index];
        pendingFile.presignedUrl = fileData.presignedUrl;
        pendingFile.key = fileData.key;
        tempFiles.push(pendingFile);
      });
      setFiles(tempFiles);
    },
    onError: () => {
      setApiButtonStatus('error');
    },
  });

  const {
    refetchAccountManagementActionTypes,
    handleConfirmChanges,
    handleBackToCoordinatorListing,
    handleBackToCoordinatorListingWithAlert,
    handleEditParticulars,
    handleConfirmChangesButton,
  } = useCreateEditViewCoordinatorAccountDetails({
    formHook,
    coordinatorProfileDetails: coordinatorProfileDetails as CoordinatorProfileResponse,
    setDeleteAccountModalMessage,
    isApproverNeededAction,
    isEditState,
    setIsEditState,
    setOpenConfirmChangesModal,
    setShouldSubmit,
    files,
    setFiles,
    setApiButtonStatus,
    setUploadFileErrorMessage,
    formDefaultValues,
    uploadFileErrorMessage,
    postRequestFilePresigned,
    uuid,
    setFilteredActionTypes,
    isCreateState,
  });

  useEffect(() => {
    if (
      watchActionType?.code === 'RESEND_ACTIVATION_EMAIL' ||
      watchActionType?.code === 'RESET_PASSWORD' ||
      !watchActionType
    ) {
      setIsApproverNeededAction(false);
      setUploadFileErrorMessage('');
    } else {
      setIsApproverNeededAction(true);
    }
  }, [watchActionType, watchActionType?.code]);

  useEffect(() => {
    const isAllFilesUploaded = files.every((f) => f.uploaded);
    const fileKeys: string[] = [''];
    files.forEach((f) => {
      if (f.key) {
        fileKeys.push(f.key);
      }
    });

    const fileUploadHasError = files.every((f) => f.hasError);
    if (fileUploadHasError) {
      setApiButtonStatus('error');
      setShouldSubmit(false);
    }

    if (isAllFilesUploaded && shouldSubmit) {
      if (!isEditState) {
        const AccountManagementActionPayload = {
          actionType: getValues('action')?.code,
          payload: {
            request: isApproverNeededAction
              ? {
                  requestorRemarks: getValues('remarks'),
                  approverUuid: getValues('approver')?.code,
                  fileKeys,
                }
              : null,
            userUuid: uuid,
            userType: 'COORDINATOR',
          },
        };
        mutatePostAccountManagementAction(AccountManagementActionPayload);
      } else if (isEdit) {
        const updateCoordinatorProfilePayload = {
          coordinatorDetail: {
            coordinatorName: getValues('coordinatorName'),
            email: getValues('email'),
            dateOfBirth:
              dayjs(getValues('dateOfBirth')).format(PAYLOAD_DATE_FORMAT) ||
              coordinatorProfileDetails?.coordinatorDetail.dateOfBirth,
            contactNumber: {
              countryCode: getValues('mobileCountryCode'),
              number: getValues('mobileContactNumber'),
            },
            salutation: getValues('salutation'),
            designation: getValues('designation'),
            organisation: {
              code: getValues('privateCentreSchool')?.uuid,
              description: getValues('privateCentreSchool')?.name,
            },
            profileUpdateApprovalModel: {
              userType:
                coordinatorProfileDetails?.coordinatorDetail.profileUpdateApprovalModel.userType,
              accountType:
                coordinatorProfileDetails?.coordinatorDetail.profileUpdateApprovalModel.accountType,
              blockStatus:
                coordinatorProfileDetails?.coordinatorDetail.profileUpdateApprovalModel.blockStatus,
              activeStatus:
                coordinatorProfileDetails?.coordinatorDetail.profileUpdateApprovalModel
                  .activeStatus,
              lockStatus:
                coordinatorProfileDetails?.coordinatorDetail.profileUpdateApprovalModel.lockStatus,
            },
            username: coordinatorProfileDetails?.coordinatorDetail.username,
          },
          requestDetail: {
            requestorRemarks: getValues('remarks'),
            approverUuid: getValues('approver')?.code,
            fileKeys,
          },
        };
        mutatePutUpdateCoordinatorProfile(updateCoordinatorProfilePayload);
      } else if (!isEdit) {
        const createCoordinatorProfilePayload = {
          coordinatorDetail: {
            coordinatorName: getValues('coordinatorName'),
            email: getValues('email'),
            dateOfBirth:
              dayjs(getValues('dateOfBirth')).format(PAYLOAD_DATE_FORMAT) ||
              coordinatorProfileDetails?.coordinatorDetail.dateOfBirth,
            contactNumber: {
              countryCode: getValues('mobileCountryCode'),
              number: getValues('mobileContactNumber'),
            },
            salutation: getValues('salutation'),
            designation: getValues('designation'),
            organisation: {
              code: getValues('privateCentreSchool')?.uuid,
              description: getValues('privateCentreSchool')?.name,
            },
            profileUpdateApprovalModel: null,
            expiryDate: dayjs(getValues('expiryDate')).format(PAYLOAD_DATE_FORMAT),
          },
          requestDetail: {
            requestorRemarks: getValues('remarks'),
            approverUuid: getValues('approver')?.code,
            fileKeys,
          },
        };
        mutatePostCreateCoordinatorProfile(createCoordinatorProfilePayload);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSubmit, files]);

  useEffect(() => {
    if (files.length) {
      setUploadFileErrorMessage('');
    }
  }, [files.length, setUploadFileErrorMessage]);

  if (coordinatorProfileDetails === undefined && isEdit) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <TitleHeader
        title={t(
          isEdit
            ? 'admin->>UserAccountManagement.ViewDetails'
            : 'admin->>UserAccountManagement.CreateCoordinatorAccount'
        )}
      />
      <ContentTemplate type="full">
        <SectionHeader
          title={t('admin->>UserAccountManagement.AccountDetails')}
          containerJustifyContent="flex-start"
        >
          {hasEditPermission && !isEditState && (
            <Box className={classes.editParticularsButton}>
              <EditButton
                editHandler={handleEditParticulars}
                label={t('admin->>UserAccountManagement.EditParticulars')}
                typographyVariant="h5"
              />
            </Box>
          )}
        </SectionHeader>

        <form onSubmit={handleSubmit(handleConfirmChangesButton)}>
          <Accordion
            defaultExpanded
            header={t('admin->>UserAccountManagement.PersonalParticulars')}
          >
            <CoordinatorPersonalParticulars
              coordinatorProfileDetails={coordinatorProfileDetails?.coordinatorDetail}
              formHook={formHook}
              isEditState={isEditState}
            />
          </Accordion>

          <Accordion defaultExpanded header={t('admin->>UserAccountManagement.ContactInformation')}>
            <CoordinatorContactInformation
              coordinatorProfileDetails={coordinatorProfileDetails?.coordinatorDetail}
              formHook={formHook}
              isEditState={isEditState}
              disabledFields={{}}
            />
          </Accordion>
          {!isEditState && (
            <ManageAccountContent
              userType={
                coordinatorProfileDetails?.coordinatorDetail.profileUpdateApprovalModel
                  ?.userType as string
              }
              accountType={
                coordinatorProfileDetails?.coordinatorDetail.profileUpdateApprovalModel
                  ?.accountType as string
              }
              activeStatus={
                coordinatorProfileDetails?.coordinatorDetail?.profileUpdateApprovalModel
                  ?.activeStatus as string
              }
              lockStatus={
                coordinatorProfileDetails?.coordinatorDetail?.profileUpdateApprovalModel
                  ?.lockStatus as string
              }
              blockStatus={
                coordinatorProfileDetails?.coordinatorDetail?.profileUpdateApprovalModel
                  ?.blockStatus as string
              }
              expiredStatus={
                coordinatorProfileDetails?.coordinatorDetail?.profileUpdateApprovalModel
                  ?.expiredStatus as string
              }
            />
          )}
          <Box className={classes.responsiveFlexContainer}>
            <Grid
              container
              className={[classes.formElementContainer, classes.formElementMarginBottom].join(' ')}
            >
              <Grid md={5} sm={12} xs={12}>
                <ToggleDisplayEditFormItem
                  isEditState={isEditState}
                  labelText={t('admin->>UserAccountManagement.ExpirationDate')}
                  valueText={
                    coordinatorProfileDetails?.coordinatorDetail?.expiryDate !== undefined &&
                    !!coordinatorProfileDetails?.coordinatorDetail?.expiryDate
                      ? stringToSemanticDisplay(
                          coordinatorProfileDetails?.coordinatorDetail?.expiryDate
                        )
                      : '-'
                  }
                  containerClassName={classes.formElementContainer}
                  EditComponent={
                    formHook && (
                      <ControlledDatePicker
                        control={control}
                        label={t('admin->>UserAccountManagement.ExpirationDate')}
                        onChange={(date) => {
                          setExpiryDate(date || '');
                          formHook?.setValue('expiryDate', date || '');
                        }}
                        inputVariant="filled"
                        error={!!formHook?.formState.errors.expiryDate}
                        helperText={formHook?.formState.errors?.expiryDate?.message?.toString()}
                        isRequired={isCreate}
                        minDate={dayjs(tomorrowDate)}
                        name="expiryDate"
                        disabled={!isCreate}
                      />
                    )
                  }
                />
              </Grid>
            </Grid>
          </Box>
          <ActionApproverRemarksContent
            filteredActionTypes={filteredActionTypes}
            apiButtonStatus={apiButtonStatus}
            postSearchApproversData={postSearchApproversData}
            files={files}
            setFiles={setFiles}
            isEditState={isEditState}
            isApproverNeededAction={isApproverNeededAction}
            formHook={formHook}
            isEdit={isEdit}
            isAdmin={false}
          />
          <AccountManagementMiscellaneousComponents
            errorMessage={errorMessage}
            uploadFileErrorMessage={uploadFileErrorMessage}
            apiButtonStatus={apiButtonStatus}
            authorities={authorities}
            setFiles={setFiles}
            isEdit={isEdit}
            isEditState={isEditState}
            setUploadFileErrorMessage={setUploadFileErrorMessage}
            setErrorMessage={setErrorMessage}
            setIsEditState={setIsEditState}
            handleBackToListing={handleBackToCoordinatorListing}
            profileDetails={coordinatorProfileDetails}
            deleteAccountModalMessage={deleteAccountModalMessage}
            setDeleteAccountModalMessage={setDeleteAccountModalMessage}
            setOpenConfirmChangesModal={setOpenConfirmChangesModal}
          />
          <ChangeRequestDialog
            open={openConfirmChangesModal}
            onClose={() => setOpenConfirmChangesModal(false)}
            onConfirm={handleConfirmChanges}
          />
        </form>
      </ContentTemplate>
    </>
  );
};
export default CreateEditViewCoordinatorAccount;
